import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { useI18n } from "../../lib/i18n";

const lineVariant = {
    hidden: { y: 80, opacity: 0 },
    show: (i) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 1.1,
            delay: 0.3 + i * 0.14,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

export default function Hero() {
    const { t } = useI18n();
    const h = t.hero;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const lines = [
        { text: h.line1 },
        { text: h.line2Pre, accent: h.line2Accent },
        { text: h.line3, dot: true },
    ];

    return (
        <section
            ref={ref}
            id="top"
            className="relative min-h-screen spotlight grain overflow-hidden"
            data-testid="hero-section"
        >
            <motion.div
                aria-hidden
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6, delay: 0.1 }}
                className="pointer-events-none absolute -top-1/4 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full"
                style={{
                    background:
                        "radial-gradient(circle at center, rgba(229,91,19,0.32) 0%, rgba(229,91,19,0.05) 30%, transparent 60%)",
                    filter: "blur(20px)",
                }}
            />

            <motion.div
                style={{ y, opacity }}
                className="container-x relative z-10 pt-28 md:pt-32"
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.05 }}
                    className="flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.24em] text-white/50"
                >
                    <span className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E55B13] animate-pulse" />
                        {h.badge}
                    </span>
                    <span className="hidden md:inline">{h.location}</span>
                </motion.div>
            </motion.div>

            <motion.div
                style={{ y, opacity }}
                className="container-x relative z-10 mt-8 md:mt-12"
            >
                <h1 className="font-heading font-light text-white tracking-[-0.04em] leading-[1.02] text-[9vw] md:text-[6.4vw] lg:text-[5.6vw]">
                    {lines.map((line, li) => (
                        <motion.span
                            key={li}
                            custom={li}
                            variants={lineVariant}
                            initial="hidden"
                            animate="show"
                            className="block"
                        >
                            {line.text}
                            {line.accent && (
                                <span className="italic font-extralight text-[#E55B13]">
                                    {line.accent}
                                </span>
                            )}
                            {line.dot && <span className="accent-dot" />}
                        </motion.span>
                    ))}
                </h1>
            </motion.div>

            <motion.div
                style={{ y, opacity }}
                className="container-x relative z-10 mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end"
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.05 }}
                    className="lg:col-span-6"
                >
                    <p className="font-body text-white/70 text-base md:text-lg leading-relaxed max-w-xl">
                        {h.subtitle.pre}
                        <span className="text-white">{h.subtitle.a}</span>
                        {h.subtitle.sep1}
                        <span className="text-white">{h.subtitle.b}</span>
                        {h.subtitle.sep2}
                        <span className="text-white">{h.subtitle.c}</span>
                        {h.subtitle.sep3}
                        <span className="text-white">{h.subtitle.d}</span>
                        {h.subtitle.sep4}
                        <span className="text-white">{h.subtitle.e}</span>
                        {h.subtitle.post}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.35 }}
                    className="lg:col-span-6 flex flex-wrap items-center gap-3 lg:justify-end"
                >
                    <a
                        href="#contact"
                        className="btn-primary inline-flex items-center gap-2 group"
                        data-testid="hero-cta-primary"
                    >
                        {h.ctaPrimary}
                        <ArrowUpRight
                            size={16}
                            className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                        />
                    </a>
                    <a
                        href="#portfolio"
                        className="btn-ghost inline-flex items-center gap-2 group"
                        data-testid="hero-cta-secondary"
                    >
                        <Play size={14} className="text-[#E55B13]" />
                        {h.ctaSecondary}
                    </a>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.6 }}
                className="container-x relative z-10 mt-14 md:mt-20 pb-10"
            >
                <div className="hairline mb-6" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
                    {h.stats.map((stat, i) => (
                        <div
                            key={i}
                            data-testid={`hero-stat-${i}`}
                            className="flex flex-col"
                        >
                            <div className="font-heading text-2xl md:text-3xl font-light text-white tracking-[-0.02em]">
                                {stat.k}
                            </div>
                            <div className="mt-1 font-mono text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/40">
                                {stat.v}
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
}
